import { ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { Constants } from "@constants";
import { ExportType } from "@fs/enums/common-enums";
import { Grid } from "@fs/enums/grid.enum";
import { FsUtil } from "@fs/services/fs-utils";
import { DataStateChangeEvent } from "@progress/kendo-angular-grid";
import { DataSourceRequestState } from '@progress/kendo-data-query';
import { ActionName } from "@shared/enums/action-name";
import { FilterProp } from "@shared/models/filterprop.model";
import { GridAction, GridColumn } from "@shared/models/grid.model";
import { PageMetaData } from "@shared/models/page-meta-data.model";
import { Subject } from "rxjs";

export abstract class GridBaseComponent {  
  pageMetaData: PageMetaData;
  gridColumns: GridColumn[];
  gridActions: GridAction[];
  gridState: DataSourceRequestState;
  tabCounter: number;  
  actionName = ActionName;
  gridLoader = true;
  detailTemplate: GridColumn;
  
  protected gridType: Grid;
  protected refreshSearch: boolean = false;
  protected isExport: boolean = false;
  protected readonly ngUnsubscribe = new Subject();
  protected exportType: ExportType;
  protected searchKey: string = "";
  protected makeRequest$ = new Subject();
  abstract setGridMetaData();
  
  constructor(type) {
    this.gridType = type;
  }

  protected getData() {
    this.refreshSearch = this.searchKey.length > 2 ? true : (this.searchKey.length == 0) ? false : this.refreshSearch;
    this.gridLoader = true;
    this.makeRequest$.next(this.searchKey);
  }
}

export abstract class GridSort extends GridBaseComponent {

  constructor(...args) {
    super(args[0]);
    this.gridState = {
      skip: 0,
      take: Constants.pageSizes[0],
      sort: this.configSort([])
    }
  }

  dataStateChange(state: DataStateChangeEvent): void {
    state.sort =
      state.sort != null
        ? state.sort[0].dir == undefined
          ? this.configSort(state.sort)
          : state.sort
        : null;
    this.sort(state);
  }
  protected sort(state: DataStateChangeEvent) {
    this.gridState = state;
    this.getData();
  }
  protected configSort(source = [], direction?: string) {
    source.splice(0, source.length);
    switch (this.gridType) {
      case Grid.Customer:
      case Grid.JobSite: {
        source.push({ field: "Jobsite.DisplayName", dir: "asc" });
        break;
      }
      case Grid.AccountingLog: {
        source.push({ field: 'SubmittedDate', dir: 'desc' });
        break;
      }
      case Grid.Product: {
        source.push({ field: 'ItemNumber', dir: 'asc' });
        break;
      }
      case Grid.Setup: {
        source.push({ field: 'Name', dir: 'asc' });
        break;
      }
      case Grid.JobSiteWorkOrder: {
        source.push({ field: "PromisedDate", dir: "desc" });
        break;
      }
      case Grid.WorkOrder: {
        source.push({ field: "PromisedDate", dir: "asc" });
        break;
      }
      case Grid.WorkOrderReview:{
        source.push({ field: "CompletedDate", dir: "asc" });
        break;
      }
      case Grid.JobSiteEquipment: {
        source.push({ field: "InstallDate", dir: "asc" });
        break;
      } 
      case Grid.ServiceAgreement: {
        source.push({ field: "AgreementName", dir: "asc" });
        break;
      }
      case Grid.JobSiteServiceAgreement: {
        source.push({ field: "EffectiveDate", dir: (direction)? direction : "desc" });
        break;
      }
      case Grid.Visit: 
      case Grid.JobSiteVisit: {
        source.push({ field: "FollowupDate", dir: "asc" });
        break;
      }
      case Grid.Estimate: {
        source.push({ field: "EstimateNo", dir: "asc" });
        break;
      }
    }
    return source;
  }
}
export abstract class GridFilter extends GridSort {
  // Angular 8+ static can be used instead
  @ViewChild("filterOptions") set filterOptions(content) {
    if(content) {
      this.filterOptionsVc = content;
      if (this.toggelFilter) 
        this.toggleFilterOptions();
      
        this.toggelFilter = false;
    }
  };

  filterOptionsVc: any;
  filterColumns = [];
  filterForm: FormGroup;   
  filterClass: string;
  toggelFilter: boolean = false;
  
  constructor(...args) {
    super(...args);
    this.filterInit();
    this.filterForm.get('searchString').valueChanges.subscribe(val => {
      if (val.trim() == "" && this.refreshSearch) {
        this.searchKey = val;
        this.getData();
      }
      else {
        this.gridState.skip = 0;
        if (val.length > 2) {
          this.searchKey = val;
          this.getData();
        }
      }
    });
  }

  applyFilter(obj: { applyAdvanceFilter: boolean, advanceFilterActive: any } = { advanceFilterActive: true, applyAdvanceFilter: null }) {
    let filters = <FormArray>this.filterForm.controls['searchFilters'];
    this.filter(obj, filters);
  }         
  clearQuickSearch() {
    this.gridState.skip = 0;
    this.gridState.take = Constants.pageSizes[0];
    this.filterForm.controls['searchString'].patchValue("");
  }
  export(val) {
    this.gridState.take = -1;
    this.gridState.skip = 0;
    this.isExport = true;
    this.exportType = val;
    this.gridLoader = true;
    this.makeRequest$.next();
  }
  toggleFilterOptions() {
    this.filterOptionsVc.DisplayToggle();
  } 
  protected filter(filterSetting, filters) {
    if (filterSetting.applyAdvanceFilter) {
      this.gridState.filter = { logic: 'and', filters: filters.value.filter(x => x.field != "") };
      if (filterSetting.advanceFilterActive) this.filterClass = "active"; else this.filterClass = "";
      this.gridState.skip = 0;
      this.gridState.take = Constants.pageSizes[0];
      FsUtil.parseFilters(this.gridState.filter.filters);
    }
    this.getData();
  }
  private filterInit() {
    this.configFilters(this.filterColumns);
    let frmbuilder = new FormBuilder();
    this.filterForm = frmbuilder.group({
      "searchFilters": frmbuilder.array([]),
      "searchString": new FormControl(""),
    });
  }
  private configFilters(source: FilterProp[]) {
    switch (this.gridType) {
      case Grid.CustomerSearch:
        source.push(new FilterProp("First Name", "FirstName", "string"));
        source.push(new FilterProp("Last Name", "LastName", "string"));
        source.push(new FilterProp("Company Name", "CompanyName", "string"));
        source.push(new FilterProp("Display Name", "DisplayName", "string"));
        source.push(new FilterProp("Address", "Address", "string"));
        source.push(new FilterProp("Phone Number", "PhoneNumber", "string"));
        source.push(new FilterProp("Email Address", "EmailAddress", "string"));
        break;
      case Grid.Category:
        source.push(new FilterProp("All Names", "AllNames", "string"));
        source.push(new FilterProp("Category Name", "CategoryName", "string"));
        source.push(new FilterProp("Sub Category Name", "SubCategoryName", "string"));
        break;
      case Grid.TaxGroup:
        source.push(new FilterProp("Accounting Integration", "MappingCode", "string"));
        source.push(new FilterProp("Active Status", "IsActive", "Boolean"));
        source.push(new FilterProp("Tax Rate", "TaxRate", "decimal"));
        break;
      case Grid.Product:
        source.push(new FilterProp("Item No.", "ItemNumber", "string"));
        source.push(new FilterProp("Item Description", "Description", "string"));
        source.push(new FilterProp("Item Category", "CategoryName", "string"));
        source.push(new FilterProp("Frequently Used", "IsFrequentlyUsed", "Boolean"));
        source.push(new FilterProp("Standard Price", "StandardPrice", "decimal"));
        source.push(new FilterProp("Preferred Supplier", "PreferredSupplier", "string"));
        source.push(new FilterProp("Supplier Item", "SupplierItemNumber", "string"));
        source.push(new FilterProp("Item Type", "Type", "string"));
        source.push(new FilterProp("Note", "Note", "string"));
        break;
      case Grid.WorkOrder:
        source.push(new FilterProp("Job Site First Name", "Jobsite.FirstName", "string"));
        source.push(new FilterProp("Job Site Last Name", "Jobsite.LastName", "string"));
        source.push(new FilterProp("Job Site Company Name", "Jobsite.CompanyName", "string"));
        source.push(new FilterProp("Job Site Display Name", "Jobsite.DisplayName", "string"));
        source.push(new FilterProp("Job Site Street Address", "Jobsite.StreetNo", "string"));
        source.push(new FilterProp("Unit/Apartment", "Jobsite.Unit", "string"));
        source.push(new FilterProp("City", "Jobsite.City", "string"));
        source.push(new FilterProp("Zip Code", "Jobsite.ZipCode", "string"));
        source.push(new FilterProp("Completed Date", "CompletedDate", "daterange"));
        source.push(new FilterProp("Service Type", "ServiceType", "string"));
        source.push(new FilterProp("Technician", "TechnicianID", "MultiSelect"));
        source.push(new FilterProp("Promised Date", "PromisedDate", "daterange"));
        source.push(new FilterProp("Department", "DepartmentID", "MultiSelect"));
        source.push(new FilterProp("State", "StatusID", "MultiSelect"));
        source.push(new FilterProp("Sub State", "SubStatusID", "MultiSelect"));
        source.push(new FilterProp("Priority", "PriorityID", "MultiSelect"));
        break;
      case Grid.WorkOrderReview:
        source.push(new FilterProp("Job Site First Name", "Jobsite.FirstName", "string"));
        source.push(new FilterProp("Job Site Last Name", "Jobsite.LastName", "string"));
        source.push(new FilterProp("Job Site Company Name", "Jobsite.CompanyName", "string"));
        source.push(new FilterProp("Job Site Display Name", "Jobsite.DisplayName", "string"));
        source.push(new FilterProp("Job Site Street Address", "Jobsite.StreetNo", "string"));
        source.push(new FilterProp("Job Site Unit/Apartment", "Jobsite.Unit", "string"));
        source.push(new FilterProp("Job Site City", "Jobsite.City", "string"));
        source.push(new FilterProp("Job Site Zip Code", "Jobsite.ZipCode", "string"));
        source.push(new FilterProp("Customer Billing First Name", "Customer.FirstName", "string"));
        source.push(new FilterProp("Customer Billing Last Name", "Customer.LastName", "string"));
        source.push(new FilterProp("Customer Billing Company Name", "Customer.CompanyName", "string"));
        source.push(new FilterProp("Customer Billing Display Name", "Customer.DisplayName", "string"));
        source.push(new FilterProp("Customer Billing Street Address", "Customer.StreetNo", "string"));
        source.push(new FilterProp("Customer Billing Unit/Apartment", "Customer.Unit", "string"));
        source.push(new FilterProp("Customer Billing City", "Customer.City", "string"));
        source.push(new FilterProp("Customer Billing Zip Code", "Customer.ZipCode", "string"));
        source.push(new FilterProp("Completed Date", "CompletedDate", "daterange"));
        source.push(new FilterProp("Service Type", "ServiceType", "string"));
        source.push(new FilterProp("Technician", "TechnicianID", "MultiSelect"));
        source.push(new FilterProp("Department", "DepartmentID", "MultiSelect"));
        source.push(new FilterProp("State", "StatusID", "MultiSelect"));
        source.push(new FilterProp("Sub State", "SubStatusID", "MultiSelect"));
        break;
      case Grid.DispatchBoard:
        source.push(new FilterProp("State", "StatusID", "MultiSelect"));
        source.push(new FilterProp("Sub State", "SubStatusID", "MultiSelect"));
        break;
      case Grid.AccountingLog:
        source.push(new FilterProp("Submitted By", "SubmittedBy", "string"));
        source.push(new FilterProp("Command", "Command", "string", ["eq", "neq"]));
        source.push(new FilterProp("Command Response", "CommandResponse", "string", ["eq", "neq"]));
        source.push(new FilterProp("Entity", "EntityName", "string", ["b", "nb", "eq", "neq"]));
        source.push(new FilterProp("Message ", "Message", "string"));
        source.push(new FilterProp("Submission Date", "SubmittedDate", "daterange"));
        source.push(new FilterProp("Status", "TransactionStatusID", "MultiSelect"));
        break;
      case Grid.Customer:
      case Grid.JobSite:
        source.push(new FilterProp("Accounting Integration", "IntegrationStatus", "string",null,true));
        source.push(new FilterProp("City", "City", "string",null,true));
        source.push(new FilterProp("Comment", "Comment", "string",null,true));
        source.push(new FilterProp("Company Name", "CompanyName", "string",null,true));
        source.push(new FilterProp("Contact First Name", "ContactFirstName", "string",null,true));
        source.push(new FilterProp("Contact Last Name", "ContactLastName", "string",null,true));
        source.push(new FilterProp("Contact Phone", "ContactNumber", "string",null,true));
        source.push(new FilterProp("Customer Since", "Customer.CustomerSince", "Date"));
        source.push(new FilterProp("Display Name", "DisplayName", "string",null,true));
        source.push(new FilterProp("Email", "ContactEmail", "string",null,true));
        source.push(new FilterProp("First Name", "FirstName", "string",null,true));
        source.push(new FilterProp("Last Name", "LastName", "string",null,true));
        source.push(new FilterProp("Payment Terms", "Customer.PaymentTermID", "MultiSelect"));
        source.push(new FilterProp("Status", "IsActive", "Boolean",null,true));
        source.push(new FilterProp("Street Address", "StreetNo", "string",null,true));
        source.push(new FilterProp("State", "State", "string",null,true));
        source.push(new FilterProp("Tax Code", "Jobsite.TaxGroupID", "MultiSelect"));
        source.push(new FilterProp("Type", "TypeID", "MultiSelect",null,true));
        source.push(new FilterProp("Unit/Apartment", "Unit", "string",null,true));
        source.push(new FilterProp("Zip", "ZipCode", "string",null,true));
        source.push(new FilterProp("Active Agreement", "ActiveAgreement", "Boolean"));
        break;
      case Grid.JobsiteSearch:
        source.push(new FilterProp("First Name", "FirstName", "string"));
        source.push(new FilterProp("Last Name", "LastName", "string"));
        source.push(new FilterProp("Company Name", "CompanyName", "string"));
        source.push(new FilterProp("City", "City", "string"));
        source.push(new FilterProp("State", "State", "string"));
        source.push(new FilterProp("Zip", "ZipCode", "string"));
        source.push(new FilterProp("Customer Since", "CustomerSince", "Date"));
        source.push(new FilterProp("Contact Phone Number", "PhoneNumber", "string"));
        source.push(new FilterProp("Email Address", "EmailAddress", "string"));
        source.push(new FilterProp("Active Status", "IsActive", "Boolean"));
        break;
      case Grid.CustomerLiteSearch:
        source.push(new FilterProp("First Name", "FirstName", "string"));
        source.push(new FilterProp("Last Name", "LastName", "string"));
        source.push(new FilterProp("Company Name", "CompanyName", "string"));
        source.push(new FilterProp("City", "City", "string"));
        source.push(new FilterProp("State", "State", "string"));
        source.push(new FilterProp("Zip", "ZipCode", "string"));
        source.push(new FilterProp("Customer Since", "CustomerSince", "Date"));
        source.push(new FilterProp("Contact Phone Number", "PhoneNumber", "string"));
        source.push(new FilterProp("Email Address", "EmailAddress", "string"));
        source.push(new FilterProp("Active Status", "IsActive", "Boolean"));
        break;
      case Grid.NewWorkOrder:
        source.push(new FilterProp("First Name", "FirstName", "string"));
        source.push(new FilterProp("Last Name", "LastName", "string"));
        source.push(new FilterProp("Company Name", "CompanyName", "string"));
        break;
      case Grid.ServiceAgreement:
        source.push(new FilterProp("Agreement No", "AgreementNo", "string"));
        source.push(new FilterProp("Agreement Name", "AgreementName", "string"));
        source.push(new FilterProp("Effective Date", "EffectiveDate", "Date"));
        source.push(new FilterProp("Expiration Date", "ExpirationDate", "Date"));
        source.push(new FilterProp("Length", "Length", "number", [], false, [{Key : "Month", Value: "Month(s"}, {Key: "Year", Value: "Year(s)"}]));
        source.push(new FilterProp("Interval", "IntervalID", "MultiSelect"));
        source.push(new FilterProp("Job Site Display Name", "JobSite.DisplayName", "string"));
        source.push(new FilterProp("Job Site Street Address", "Jobsite.StreetNo", "string"));
        source.push(new FilterProp("Job Site Unit/Apartment", "Jobsite.Unit", "string"));
        source.push(new FilterProp("Job Site City", "Jobsite.City", "string"));
        source.push(new FilterProp("Job Site State", "Jobsite.State", "string"));
        source.push(new FilterProp("Job Site Zip Code", "Jobsite.ZipCode", "string"));        
        source.push(new FilterProp("Status", "Status", "string"));
        break;
      case Grid.Visit:
        source.push(new FilterProp("Agreement No", "AgreementNumber", "string"));
        source.push(new FilterProp("Job Site Display Name", "Jobsite.DisplayName", "string"));
        source.push(new FilterProp("Job Site Street Address", "Jobsite.StreetNo", "string"));
        source.push(new FilterProp("Job Site Unit/Apartment", "Jobsite.Unit", "string"));
        source.push(new FilterProp("Job Site City", "Jobsite.City", "string"));
        source.push(new FilterProp("Job Site State", "Jobsite.State", "string"));
        source.push(new FilterProp("Job Site Zip Code", "Jobsite.ZipCode", "string"));        
        source.push(new FilterProp("Follow-up Date", "FollowupDate", "Date"));
        source.push(new FilterProp("Proposed Date", "ProposedDate", "Date"));
        source.push(new FilterProp("Status", "VisitStatusID", "MultiSelect"));
        source.push(new FilterProp("No. of Attempts", "AttemptCount", "number"));
        source.push(new FilterProp("Job Site Contact Phone", "Jobsite.ContactNumber", "string"));
        break;
      case Grid.Estimate:
        source.push(new FilterProp("Job Site Display Name", "Jobsite.DisplayName", "string"));
        source.push(new FilterProp("Job Site Street Address", "Jobsite.StreetNo", "string"));
        source.push(new FilterProp("Job Site Unit/Apartment", "Jobsite.Unit", "string"));
        source.push(new FilterProp("Job Site City", "Jobsite.City", "string"));
        source.push(new FilterProp("Job Site State", "Jobsite.State", "string"));
        source.push(new FilterProp("Job Site Zip Code", "Jobsite.ZipCode", "string"));
        source.push(new FilterProp("Estimate State", "EstimateStatusID", "MultiSelect"));
        source.push(new FilterProp("Appointment State", "AppointmentSubStatusID", "MultiSelect"));
        break;
    }
  }
}